import { useEffect, useState, useCallback, useRef } from 'react';
import { getAuthToken } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/constants';
import {
  ArrowPathIcon,
  EyeIcon,
  XMarkIcon,
  TrashIcon,
  Squares2X2Icon,
  ListBulletIcon,
  ArrowUpOnSquareIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';

type CloudinaryImage = {
  public_id: string;
  url: string;
  width?: number;
  height?: number;
  format?: string;
  folder?: string;
  title?: string;
  caption?: string;
  location?: string;
  tags?: string[];
  isPublished?: boolean;
};

const thumb = (url: string) => {
  try {
    const u = new URL(url);
    const parts = u.pathname.split('/image/upload/');
    if (parts.length === 2) {
      u.pathname = `${parts[0]}/image/upload/c_fill,f_auto,q_auto,w_400,h_300/${parts[1]}`;
      return u.toString();
    }
    return url;
  } catch {
    return url;
  }
};

const AdminGallery = () => {
  const [images, setImages] = useState<CloudinaryImage[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [mode, setMode] = useState<'grid' | 'list'>('list');
  const [aiBusy, setAiBusy] = useState(false);
  const [publishedIds, setPublishedIds] = useState<Set<string>>(new Set());
  const [folder, setFolder] = useState(
    'home/mermaids-blog/my-web-eliments/cover-photo-hero'
  );

  const fetchImages = useCallback(
    async (cursor?: string, retryCount = 0) => {
      try {
        const token = getAuthToken();
        const params = new URLSearchParams();
        params.set('limit', '40');
        params.set('folder', folder);
        // Avoid sending next if empty/invalid
        if (cursor && cursor !== 'null' && cursor !== 'undefined')
          params.set('next', cursor);
        const res = await fetch(
          `${API_ENDPOINTS.gallery}/cloudinary?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        const json = await res.json();
        if (!json.success) {
          // Handle rate limiting specifically
          if (res.status === 429) {
            if (retryCount < 2) {
              // Exponential backoff: wait 2^retryCount * 2 seconds
              const waitTime = Math.pow(2, retryCount) * 2000;
              console.log(`Rate limited, retrying in ${waitTime / 1000}s...`);
              await new Promise(resolve => setTimeout(resolve, waitTime));
              return fetchImages(cursor, retryCount + 1);
            }
            throw new Error(
              `⏰ ${json.error || 'Cloudinary rate limit exceeded. Please wait and try again.'}`
            );
          }
          throw new Error(json.error || 'Failed to fetch Cloudinary images');
        }
        const mapped: CloudinaryImage[] = (json.data || []).map((i: any) => ({
          ...i,
          isPublished: publishedIds.has(i.public_id),
        }));
        setImages(prev => (cursor ? [...prev, ...mapped] : mapped));
        setNextCursor(json.pagination?.next || null);
      } catch (e: any) {
        setError(e.message || 'Failed to load images');
      }
    },
    [publishedIds, folder]
  );

  const fetchImagesWithPublishedIds = async (
    publishedSet: Set<string>,
    cursor?: string,
    retryCount = 0
  ) => {
    try {
      const token = getAuthToken();
      const params = new URLSearchParams();
      params.set('limit', '40');
      params.set('folder', folder);
      // Avoid sending next if empty/invalid
      if (cursor && cursor !== 'null' && cursor !== 'undefined')
        params.set('next', cursor);
      const res = await fetch(
        `${API_ENDPOINTS.gallery}/cloudinary?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const json = await res.json();
      if (!json.success) {
        // Handle rate limiting specifically
        if (res.status === 429) {
          if (retryCount < 2) {
            // Exponential backoff: wait 2^retryCount * 2 seconds
            const waitTime = Math.pow(2, retryCount) * 2000;
            console.log(`Rate limited, retrying in ${waitTime / 1000}s...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
            return fetchImagesWithPublishedIds(
              publishedSet,
              cursor,
              retryCount + 1
            );
          }
          throw new Error(
            `⏰ ${json.error || 'Cloudinary rate limit exceeded. Please wait and try again.'}`
          );
        }
        throw new Error(json.error || 'Failed to fetch Cloudinary images');
      }
      const mapped: CloudinaryImage[] = (json.data || []).map((i: any) => ({
        ...i,
        isPublished: publishedSet.has(i.public_id),
      }));
      setImages(prev => (cursor ? [...prev, ...mapped] : mapped));
      setNextCursor(json.pagination?.next || null);
    } catch (e: any) {
      setError(e.message || 'Failed to load images');
    }
  };

  const fetchPublished = useCallback(async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_ENDPOINTS.gallery}/admin?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const j = await res.json();
      if (j.success) {
        setPublishedIds(
          new Set<string>(j.data.map((it: any) => it.cloudinary_public_id))
        );
      }
    } catch {}
  }, []);

  // Ref to track if initial load is done to prevent infinite loops
  const initialLoadDone = useRef(false);

  useEffect(() => {
    const init = async () => {
      if (initialLoadDone.current) return;

      setLoading(true);
      setError(null);

      try {
        // First fetch published IDs
        const token = getAuthToken();
        const res = await fetch(`${API_ENDPOINTS.gallery}/admin?limit=1000`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const j = await res.json();
        if (j.success) {
          const publishedSet = new Set<string>(
            j.data
              .filter((it: any) => it.is_published)
              .map((it: any) => it.cloudinary_public_id)
          );
          setPublishedIds(publishedSet);

          // Then fetch images with the published IDs
          await fetchImagesWithPublishedIds(publishedSet);
        }
      } catch (err: any) {
        setError('Failed to load gallery data');
        console.error('Gallery init error:', err);
      } finally {
        setLoading(false);
        initialLoadDone.current = true;
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array to run only once

  const toggleSelect = (id: string) => {
    setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const publishSelected = async () => {
    try {
      setError(null);
      setSuccess(null);
      const token = getAuthToken();
      const toPublish = images
        .filter(i => selected[i.public_id])
        .map(i => ({
          public_id: i.public_id,
          url: i.url,
          width: i.width,
          height: i.height,
          format: i.format,
          folder: i.folder,
          title: i.title,
          caption: i.caption,
          location: i.location,
          tags: i.tags,
        }));
      if (toPublish.length === 0) {
        setError('Please select at least one image');
        return;
      }
      const res = await fetch(`${API_ENDPOINTS.gallery}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ images: toPublish }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Failed');
      setSuccess(`Published ${json.data.length} image(s) to gallery`);
      setSelected({});
      const pub = new Set(publishedIds);
      json.data.forEach((it: any) => pub.add(it.cloudinary_public_id));
      setPublishedIds(pub);
      setImages(prev =>
        prev.map(i => (pub.has(i.public_id) ? { ...i, isPublished: true } : i))
      );
    } catch (e: any) {
      setError(e.message || 'Failed to publish');
    }
  };

  const uploadNew = async (file: File) => {
    const token = getAuthToken();
    const fd = new FormData();
    fd.append('image', file);
    const params = new URLSearchParams();
    params.set('folder', folder);
    const res = await fetch(
      `${API_ENDPOINTS.gallery}/upload?${params.toString()}`,
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      }
    );
    const j = await res.json();
    if (j.success) {
      setImages(prev => [j.data, ...prev]);
      setSuccess('Image uploaded');
    } else {
      setError(j.error || 'Upload failed');
    }
  };

  const deleteOne = async (public_id: string) => {
    const token = getAuthToken();
    const res = await fetch(`${API_ENDPOINTS.gallery}/delete`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ public_id, alsoCloudinary: true }),
    });
    const j = await res.json();
    if (j.success) {
      setImages(prev => prev.filter(i => i.public_id !== public_id));
    } else {
      setError(j.error || 'Delete failed');
    }
  };

  const publishOne = async (img: CloudinaryImage) => {
    try {
      const token = getAuthToken();
      const body = {
        images: [
          {
            public_id: img.public_id,
            url: img.url,
            width: img.width,
            height: img.height,
            format: img.format,
            folder: img.folder,
            title: img.title,
            caption: img.caption,
            location: img.location,
            tags: img.tags,
          },
        ],
      };
      const res = await fetch(`${API_ENDPOINTS.gallery}/publish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Failed to publish');
      const pub = new Set(publishedIds);
      pub.add(img.public_id);
      setPublishedIds(pub);
      setImages(prev =>
        prev.map(i =>
          i.public_id === img.public_id ? { ...i, isPublished: true } : i
        )
      );
      setSuccess('Image published');
    } catch (e: any) {
      setError(e.message || 'Publish failed');
    }
  };

  const unpublishOne = async (public_id: string) => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_ENDPOINTS.gallery}/unpublish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ public_ids: [public_id] }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Failed to unpublish');
      const pub = new Set(publishedIds);
      pub.delete(public_id);
      setPublishedIds(pub);
      setImages(prev =>
        prev.map(i =>
          i.public_id === public_id ? { ...i, isPublished: false } : i
        )
      );
      setSuccess('Image unpublished');
    } catch (e: any) {
      setError(e.message || 'Unpublish failed');
    }
  };

  const unpublishSelected = async () => {
    try {
      const token = getAuthToken();
      const ids = Object.entries(selected)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (ids.length === 0) {
        setError('Select image(s) to unpublish');
        return;
      }
      const res = await fetch(`${API_ENDPOINTS.gallery}/unpublish`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ public_ids: ids }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'Failed to unpublish');
      setSuccess(`Unpublished ${j.data?.length || ids.length} image(s)`);
      setSelected({});
      const pub = new Set(publishedIds);
      ids.forEach(id => pub.delete(id));
      setPublishedIds(pub);
      setImages(prev =>
        prev.map(i =>
          ids.includes(i.public_id) ? { ...i, isPublished: false } : i
        )
      );
    } catch (e: any) {
      setError(e.message || 'Failed to unpublish');
    }
  };

  const generateAIFor = async (img: CloudinaryImage) => {
    try {
      setAiBusy(true);
      const token = getAuthToken();
      const filename = (img.public_id || img.url || '').split('/').pop() || '';
      const context = {
        image_url: img.url,
        filename,
        existing_title: img.title || '',
        existing_location: img.location || '',
        existing_caption: img.caption || '',
        existing_tags: img.tags || [],
        // Encourage variation by adding light random seeds
        seed: Math.floor(Math.random() * 1e9),
        style: ['playful', 'romantic', 'adventurous', 'storyteller'][
          Math.floor(Math.random() * 4)
        ],
      };

      const prompt = `You are helping a travel blog write metadata for a photo.
Return ONLY valid JSON (no markdown fences, no commentary) with keys: title (<=60 chars), caption (<=160 chars), location (short human-friendly), tags (array of 3-6 short lowercase keywords, no #).
Use provided context if good; otherwise infer from filename and url. Avoid HTML/markdown. Tone: warm, adventurous. Make it distinct each time by varying word choice and phrasing.
Context: ${JSON.stringify(context)}`;

      const res = await fetch(`${API_ENDPOINTS.ai}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ prompt, type: 'custom' }),
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error || 'AI generation failed');

      let text: string = (j.data?.content || '').trim();
      // Strip code fences if any
      text = text.replace(/^```json\s*([\s\S]*?)\s*```$/i, '$1').trim();
      text = text
        .replace(/^```[a-z]*\s*/i, '')
        .replace(/```\s*$/i, '')
        .trim();

      // Try to extract JSON substring if extra text present
      const firstBrace = text.indexOf('{');
      const lastBrace = text.lastIndexOf('}');
      if (firstBrace > -1 && lastBrace > firstBrace) {
        text = text.substring(firstBrace, lastBrace + 1);
      }

      let parsed: any;
      try {
        parsed = JSON.parse(text);
      } catch {
        // Fallback: naive parse lines
        const lines = text
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean);
        parsed = {
          title: (lines[0] || '').slice(0, 60),
          caption: (lines.slice(1).join(' ') || '').slice(0, 160),
          location: img.location || '',
          tags: img.tags || [],
        };
      }

      const clean = (s?: string) =>
        (s || '')
          .replace(/<[^>]*>/g, '')
          .replace(/[`*_#>]/g, '')
          .trim();
      const newTitle = clean(parsed.title).slice(0, 60);
      const newCaption = clean(parsed.caption).slice(0, 160);
      const newLocation =
        clean(parsed.location).slice(0, 60) || img.location || '';
      const newTags: string[] = Array.isArray(parsed.tags)
        ? parsed.tags
            .map((t: any) => String(t).toLowerCase().replace(/#/g, '').trim())
            .filter(Boolean)
            .slice(0, 6)
        : img.tags || [];

      setImages(prev =>
        prev.map(it =>
          it.public_id === img.public_id
            ? {
                ...it,
                title: newTitle || it.title,
                caption: newCaption || it.caption,
                location: newLocation || it.location,
                tags: newTags.length ? newTags : it.tags,
              }
            : it
        )
      );
    } catch (e: any) {
      setError(e.message || 'AI generation failed');
    } finally {
      setAiBusy(false);
    }
  };

  const onMetaChange = (
    id: string,
    field: keyof CloudinaryImage,
    value: any
  ) => {
    setImages(prev =>
      prev.map(i => (i.public_id === id ? { ...i, [field]: value } : i))
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-display font-semibold text-gray-900">
          Gallery
        </h2>
        <div className="flex items-center gap-2">
          <label className="inline-flex items-center gap-2 text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200 cursor-pointer">
            <ArrowUpOnSquareIcon className="w-4 h-4" /> Upload
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) uploadNew(f);
              }}
            />
          </label>
          <button
            onClick={() => setMode(m => (m === 'grid' ? 'list' : 'grid'))}
            className="inline-flex items-center gap-2 text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200"
          >
            {mode === 'grid' ? (
              <ListBulletIcon className="w-4 h-4" />
            ) : (
              <Squares2X2Icon className="w-4 h-4" />
            )}{' '}
            {mode === 'grid' ? 'List' : 'Grid'}
          </button>
          <button
            onClick={async () => {
              setLoading(true);
              setImages([]);
              setNextCursor(null);
              setError(null);
              try {
                await fetchPublished();
                await fetchImages();
              } catch (err) {
                setError('Failed to refresh gallery');
              } finally {
                setLoading(false);
              }
            }}
            className="inline-flex items-center gap-2 text-sm bg-gray-100 text-gray-700 px-3 py-2 rounded-lg hover:bg-gray-200"
          >
            <ArrowPathIcon className="w-4 h-4" /> Refresh
          </button>
          <button
            onClick={publishSelected}
            className="text-sm bg-ocean-500 text-white px-4 py-2 rounded-lg hover:bg-ocean-600 disabled:opacity-50"
            disabled={Object.values(selected).every(v => !v)}
          >
            Publish to Gallery
          </button>
          <button
            onClick={unpublishSelected}
            className="text-sm bg-amber-500 text-white px-4 py-2 rounded-lg hover:bg-amber-600 disabled:opacity-50"
            disabled={Object.values(selected).every(v => !v)}
          >
            Unpublish
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2 pb-4 border-b">
        <label
          htmlFor="cloudinary-folder"
          className="text-sm font-medium text-gray-700 whitespace-nowrap"
        >
          Cloudinary Folder:
        </label>
        <input
          id="cloudinary-folder"
          type="text"
          value={folder}
          onChange={e => setFolder(e.target.value)}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-ocean-500 focus:border-ocean-500"
          placeholder="e.g., home/mermaids-blog/my-web-eliments/cover-photo-hero"
        />
        <button
          onClick={() => {
            setLoading(true);
            setImages([]);
            setNextCursor(null);
            setError(null);
            fetchImages().finally(() => setLoading(false));
          }}
          className="inline-flex items-center gap-2 text-sm bg-ocean-500 text-white px-4 py-2 rounded-lg hover:bg-ocean-600 disabled:opacity-50"
        >
          <ArrowPathIcon className="w-4 h-4" /> Load
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-700">
          {success}
        </div>
      )}

      {/* Grid/List */}
      {loading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <div
              key={i}
              className="aspect-square rounded-xl bg-gray-100 animate-pulse"
            />
          ))}
        </div>
      ) : mode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map(img => (
            <div
              key={img.public_id}
              className={`relative group overflow-hidden rounded-xl shadow-sm border ${img.isPublished ? 'border-green-300' : 'border-gray-200'}`}
            >
              <img
                src={thumb(img.url)}
                alt={img.public_id}
                loading="lazy"
                className="w-full h-48 object-cover transition-transform duration-300 group-hover:scale-105 cursor-pointer"
                onClick={() => setPreview(img.url)}
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors"></div>
              <label className="absolute top-2 left-2 bg-white/90 backdrop-blur rounded-md px-2 py-1 text-xs font-medium shadow">
                <input
                  type="checkbox"
                  className="mr-2 align-middle"
                  checked={!!selected[img.public_id]}
                  onChange={() => toggleSelect(img.public_id)}
                />
                Select
              </label>
              {img.isPublished && (
                <span className="absolute top-2 right-2 bg-green-600 text-white text-[10px] px-2 py-0.5 rounded-full shadow">
                  Published
                </span>
              )}
              <div className="absolute top-2 right-2 flex gap-2">
                <button
                  title="AI"
                  onClick={() => generateAIFor(img)}
                  className="bg-white/90 p-1 rounded shadow"
                >
                  <SparklesIcon className="w-5 h-5 text-purple-600" />
                </button>
                <button
                  title="Delete"
                  onClick={() => deleteOne(img.public_id)}
                  className="bg-white/90 p-1 rounded shadow"
                >
                  <TrashIcon className="w-5 h-5 text-red-600" />
                </button>
              </div>
              <div className="p-3 text-sm">
                <input
                  value={img.title || ''}
                  onChange={e =>
                    onMetaChange(img.public_id, 'title', e.target.value)
                  }
                  placeholder="Title"
                  className="w-full border rounded px-2 py-1 mb-2"
                />
                <input
                  value={img.location || ''}
                  onChange={e =>
                    onMetaChange(img.public_id, 'location', e.target.value)
                  }
                  placeholder="Location"
                  className="w-full border rounded px-2 py-1 mb-2"
                />
                <textarea
                  value={img.caption || ''}
                  onChange={e =>
                    onMetaChange(img.public_id, 'caption', e.target.value)
                  }
                  placeholder="Caption / story"
                  className="w-full border rounded px-2 py-1"
                  rows={2}
                />
                <div className="mt-2 flex items-center gap-2">
                  {!img.isPublished ? (
                    <button
                      onClick={() => publishOne(img)}
                      className="text-xs px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700"
                    >
                      Publish
                    </button>
                  ) : (
                    <button
                      onClick={() => unpublishOne(img.public_id)}
                      className="text-xs px-3 py-1 rounded bg-amber-500 text-white hover:bg-amber-600"
                    >
                      Unpublish
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="divide-y border rounded-lg bg-white">
          {images.map(img => (
            <div key={img.public_id} className="p-3 flex items-start gap-3">
              <img
                src={thumb(img.url)}
                alt="thumb"
                className="w-28 h-20 object-cover rounded"
              />
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <label className="text-xs bg-gray-100 px-2 py-1 rounded">
                    <input
                      type="checkbox"
                      className="mr-2"
                      checked={!!selected[img.public_id]}
                      onChange={() => toggleSelect(img.public_id)}
                    />{' '}
                    Select
                  </label>
                  <span
                    className={`text-xs px-2 py-1 rounded ${img.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}
                  >
                    {img.isPublished ? 'Published' : 'Not published'}
                  </span>
                  <button
                    onClick={() => generateAIFor(img)}
                    className="text-xs bg-purple-50 text-purple-700 px-2 py-1 rounded inline-flex items-center gap-1"
                  >
                    <SparklesIcon className="w-4 h-4" /> AI
                  </button>
                  <button
                    onClick={() => deleteOne(img.public_id)}
                    className="text-xs bg-red-50 text-red-700 px-2 py-1 rounded inline-flex items-center gap-1"
                  >
                    <TrashIcon className="w-4 h-4" /> Delete
                  </button>
                  {!img.isPublished ? (
                    <button
                      onClick={() => publishOne(img)}
                      className="text-xs bg-green-600 text-white px-2.5 py-1 rounded"
                    >
                      Publish
                    </button>
                  ) : (
                    <button
                      onClick={() => unpublishOne(img.public_id)}
                      className="text-xs bg-amber-500 text-white px-2.5 py-1 rounded"
                    >
                      Unpublish
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                  <input
                    value={img.title || ''}
                    onChange={e =>
                      onMetaChange(img.public_id, 'title', e.target.value)
                    }
                    placeholder="Title"
                    className="border rounded px-2 py-1"
                  />
                  <input
                    value={img.location || ''}
                    onChange={e =>
                      onMetaChange(img.public_id, 'location', e.target.value)
                    }
                    placeholder="Location"
                    className="border rounded px-2 py-1"
                  />
                  <input
                    value={(img.tags || []).join(', ')}
                    onChange={e =>
                      onMetaChange(
                        img.public_id,
                        'tags',
                        e.target.value
                          .split(',')
                          .map(s => s.trim())
                          .filter(Boolean)
                      )
                    }
                    placeholder="Tags (comma separated)"
                    className="border rounded px-2 py-1"
                  />
                </div>
                <textarea
                  value={img.caption || ''}
                  onChange={e =>
                    onMetaChange(img.public_id, 'caption', e.target.value)
                  }
                  placeholder="Caption / story"
                  className="w-full border rounded px-2 py-1 mt-2"
                  rows={2}
                />
              </div>
              <button
                onClick={() => setPreview(img.url)}
                className="ml-2 bg-gray-100 hover:bg-gray-200 rounded p-2"
              >
                <EyeIcon className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}

      {nextCursor && (
        <div className="flex justify-center">
          <button
            onClick={() => {
              setLoadingMore(true);
              fetchImagesWithPublishedIds(publishedIds, nextCursor).finally(
                () => setLoadingMore(false)
              );
            }}
            className="mt-2 text-sm bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200"
          >
            {loadingMore ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}

      {/* Preview Modal */}
      {preview && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="relative max-w-4xl w-full">
            <button
              onClick={() => setPreview(null)}
              className="absolute -top-3 -right-3 bg-white text-gray-700 rounded-full p-1 shadow"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>
            <img
              src={preview}
              alt="Preview"
              className="w-full h-auto max-h-[80vh] object-contain rounded"
            />
          </div>
        </div>
      )}

      {aiBusy && (
        <div className="fixed bottom-4 right-4 bg-purple-600 text-white px-3 py-2 rounded shadow">
          AI writing…
        </div>
      )}
    </div>
  );
};

export default AdminGallery;
